import { getContractABI, getContractAddress } from "../utils/abiHelper";
import { getWeb3IfAvailable, parseError, toNumber, toRealNumber } from "../web3Utils";
import { ContractService } from "./ContractService";
import { NftTokenService } from "./NftTokenService";
import axios from "axios";

export class NftStakingService extends ContractService {

    constructor(args = {}) {
        super();
        this.contractType = 'NftStaking'
        this.abi = getContractABI(this.contractType);
        this.addHooks();
    }

    async preHook() {
        this.web3 = await getWeb3IfAvailable()
        this.accounts = await this.web3.eth.getAccounts();
        this.contractAddress = await getContractAddress(this.contractType);
        this.contractInstance = new this.web3.eth.Contract(
            this.abi,
            this.contractAddress
        );
    }

    async getAll({
        limit = 10,
        skip = 0
    }) {

        try {

            const tokenIdsMap = new Map();
            const nftTokenService = new NftTokenService();

            const tokenIds = await Promise.allSettled(
                Array(limit)
                .fill(0)
                .map(( _, index) => {
                    tokenIdsMap.set( index, {});
                    return this.getStakedNftByIndex(
                        this.accounts[0],
                        skip + index 
                    )
                })
            );

            const tokenURIs = await Promise.allSettled(
                tokenIds
                .map((tokenId, index) => {
                    const tokenIdInfo = tokenIdsMap.get( index );
                    if(tokenId.value) {
                        tokenIdsMap.set( index, { ...tokenIdInfo, tokenId: tokenId.value });
                        return nftTokenService.tokenURI(tokenId.value);
                    }
                    return Promise.resolve();
                })
            );
            
            const nfts = await Promise.allSettled(
                tokenURIs
                .map((tokenURI, index) => {
                    const tokenIdInfo = tokenIdsMap.get( index );
                    if(tokenURI.value) {
                        tokenIdsMap.set( index, { ...tokenIdInfo, tokenURI: tokenURI.value });
                        return axios.get(tokenURI.value)
                    }
                    return Promise.resolve();
                })
            );
            
            const stakeInfoArr = await Promise.allSettled(
                nfts
                .map((nft, index) => {
                    const tokenIdInfo = tokenIdsMap.get( index );
                    if(nft.value) {
                        tokenIdsMap.set( index, { ...tokenIdInfo, ...nft.value.data });
                        return this.getStakeInfo(this.accounts[0], tokenIdInfo.tokenId)
                    }
                    return Promise.resolve();
                })
            );

            const estimates = await Promise.allSettled(
                stakeInfoArr
                .map((stakeInfo, index) => {
                    const tokenIdInfo = tokenIdsMap.get( index );
                    if(stakeInfo.value) {
                        tokenIdsMap.set( index, { ...tokenIdInfo, stakeInfo: stakeInfo.value });
                        return this.estimate(this.accounts[0], tokenIdInfo.tokenId)
                    }
                    return Promise.resolve();
                })
            );

          
            for(let index = 0; index < estimates.length; index++) {
                const tokenIdInfo = tokenIdsMap.get( index );
                if(estimates[index].value) {
                    tokenIdsMap.set( index, { ...tokenIdInfo, estimate: estimates[index].value });
                }
            }

            return [...tokenIdsMap.values()].filter((resp) => resp.name);
         } catch(err) {
             console.log('Caught error while fetching NFTs', err)
            throw parseError(err);
        }
    }
    
    async getStakedNftByIndex(userAddress = this.accounts[0], index = 0) {
        try{
            const nftTokenId = await this.contractInstance.methods.getStakedNftByIndex(userAddress, index).call();
            return nftTokenId;
        } catch(err) {
            throw parseError(err);
        }
    }

    async getUserInfo(userAddress = this.accounts[0]) {
        try{
            const userInfo = await this.contractInstance.methods.getUserInfo(userAddress).call();
            return {
                totalStakeAmount: toRealNumber(userInfo.totalStakeAmount),
                totalRewardClaimed: toNumber(userInfo.totalRewardClaimed),
            }
        } catch(err) {
            throw parseError(err);
        }
    }

    async getStakeInfo(userAddress = this.accounts[0], tokenId = 0) {
        
        if(!tokenId || isNaN(tokenId)) throw new Error('Invalid Token Id.');

        try{
            const stakeInfo = await this.contractInstance.methods.getStakeInfo(userAddress, tokenId).call();

            return {
                startTime: toRealNumber(stakeInfo.startTime),
                periodInDays: toRealNumber(stakeInfo.periodInDays),
                totalRewardClaimed: toNumber(stakeInfo.totalRewardClaimed)
            }
        } catch(err) {
            throw parseError(err);
        }
    }

    async estimate(userAddress = this.accounts[0], tokenId = 0) {
        if(!tokenId || isNaN(tokenId)) throw new Error('Invalid Token Id.');

        try{
            const stakeEstimate = await this.contractInstance.methods.estimate(userAddress, tokenId).call();
           
            return {
                reward: toNumber(stakeEstimate.reward),
            }
        } catch(err) {
            throw parseError(err);
        }
    }

    async stakeBatch({
        nftList = [],
        periodInDays = 0
    }) {
        
        if(!periodInDays) {
            throw Error('Invalid Period Given');
        }

        let inventoryIds = [], periodInDaysArr = [], tokenIds = [];
        for(let nft of nftList) {
            if(nft.tokenId && nft.inventoryId) {
                inventoryIds.push(nft.inventoryId);
                tokenIds.push(nft.tokenId);
                periodInDaysArr.push(periodInDays);
            }
        }

        if(!inventoryIds.length) {
            throw Error('Invalid Data Received For Batching!');
        }

        try {
        
            const gasPrice = await this.web3.eth.getGasPrice();

            let args = [
                inventoryIds,
                tokenIds,
                periodInDaysArr
            ], options = {
                gasPrice,
                from: this.accounts[0]
            };

            const estimatedGas = await this.contractInstance
                .methods
                .stakeBatch( ...args)
                .estimateGas(options);
             
            options['gas'] = estimatedGas + 5000;
            await this.contractInstance
                .methods
                .stakeBatch( ...args )
                .send(options);
        } catch(err) {
            throw parseError(err);
        }
        return 'NFT Tokens Batch Staked Successfully!';
    }

    async unStake( params = {
        tokenId: 0
    } ) {
        const { tokenId } = params;

        if(!tokenId || isNaN(tokenId)) throw new Error('Invalid Token Id.');

        try {
            const gasPrice = await this.web3.eth.getGasPrice();

            let options = {
                gasPrice,
                from: this.accounts[0]
            };

            const estimatedGas = await this.contractInstance
                .methods
                .unStake(Number(tokenId))
                .estimateGas(options);
             
            options['gas'] = estimatedGas + 5000;
            await this.contractInstance
                .methods
                .unStake(Number(tokenId))
                .send(options);
        } catch(err) {
            throw parseError(err);
        }
        return 'Tokens unstaked successfully!';
    }

    async claim( params = {
        tokenId: 0
    } ) {
        const { tokenId } = params;

        if(!tokenId || isNaN(tokenId)) throw new Error('Invalid Token Id.');

        try {
            const gasPrice = await this.web3.eth.getGasPrice();

            let options = {
                gasPrice,
                from: this.accounts[0]
            };

            const estimatedGas = await this.contractInstance.methods
                .claim(Number(tokenId))
                .estimateGas(options);
             
            options['gas'] = estimatedGas + 5000;

            await this.contractInstance
                .methods
                .claim(Number(tokenId))
                .send(options);
        } catch(err) {
            throw parseError(err);
        }
        return 'Reward claimed successfully!';
    }

    async isApprovedForAll(
        userAddress = this.accounts[0]
    ) {

        const nftTokenService = new NftTokenService();

        const hasApprovalForAll = await nftTokenService.isApprovedForAll(userAddress, this.contractAddress);
        return hasApprovalForAll;
    }

    async setApprovalForAll(
        value = true
    ) {
        const nftTokenService = new NftTokenService();
        return await nftTokenService.setApprovalForAll(this.contractAddress, value);
    }
}